/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from './ui/sheet';
import { useIsMobile } from './ui/use-mobile';
import { 
  BookOpen, 
  Calendar, 
  FileText, 
  Download,
  Clock,
  MapPin,
  User,
  Archive,
  Bot,
  Menu,
  Home,
  GraduationCap,
  ChevronRight,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { SubjectsOffering } from './student/SubjectsOffering';
import { DigitalArchive } from './DigitalArchive';
import { AIAssistant } from './AIAssistant';
//import { ConnectionStatusBanner } from './ConnectionStatusBanner';
import { User as UserType } from '../types';
import { getCurrentAcademicPeriod } from '../utils/academicYear';
import { ARCHIVE_TARGET_EVENT, readArchiveTargetFromUrl } from '../utils/notificationLinks';

interface StudentDashboardProps {
  user: UserType;
}

export function StudentDashboard({ user }: StudentDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  const menuItems = [
    { value: 'overview', label: 'Overview', icon: Home },
    { value: 'subjects', label: 'Subjects Offered', icon: BookOpen },
    { value: 'archive', label: 'Document Archive', icon: Archive }
  ];

  const handleMenuItemClick = (value: string) => {
    setActiveTab(value);
    setIsMenuOpen(false);
  };

  const getCurrentPageTitle = () => {
    const item = menuItems.find(item => item.value === activeTab);
    return item ? item.label : 'Overview';
  };

  // TODO: Fetch enrolled subjects from API
  const enrolledSubjects: any[] = [];
  const totalUnits = enrolledSubjects.reduce((sum, subject) => sum + (subject?.units || 0), 0);

  // Compute the current academic year and semester dynamically
    const { academicYear, semesterLabel: currentSemester } = getCurrentAcademicPeriod();
  
    // Helper for year level label
    const getYearLevelLabel = (level: number | undefined) => {
      if (!level) return 'N/A';
      if (level === 1) return '1st Year';
      if (level === 2) return '2nd Year';
      if (level === 3) return '3rd Year';
      return `Year ${level}`;
    };

    const stats = [
      { 
        title: 'Year Level', 
        value: getYearLevelLabel(user.yearLevel), 
        icon: User, 
        color: 'bg-gradient-to-br from-purple-400 to-purple-600',
        tooltip: 'Your current year level in the program.'
      },
      { 
        title: 'Program', 
        value: user.course || 'N/A', 
        icon: BookOpen, 
        color: 'bg-gradient-to-br from-blue-400 to-blue-600',
        tooltip: 'Your enrolled course or program.'
      },
      { 
        title: 'Current Semester', 
        value: currentSemester, 
        icon: FileText, 
        color: 'bg-gradient-to-br from-orange-400 to-orange-600',
        tooltip: 'The current academic semester.'
      },
      {
        title: 'Academic Year',
        value: `S.Y. ${academicYear}`,
        icon: GraduationCap,
        color: 'bg-gradient-to-br from-green-400 to-green-600',
        tooltip: 'The current academic year.'
      }
    ];



  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || 'Unknown';
  };

  useEffect(() => {
    if (readArchiveTargetFromUrl()) {
      setActiveTab('archive');
    }

    const handleOpenArchiveTarget = () => {
      setActiveTab('archive');
    };

    window.addEventListener(ARCHIVE_TARGET_EVENT, handleOpenArchiveTarget);
    return () => {
      window.removeEventListener(ARCHIVE_TARGET_EVENT, handleOpenArchiveTarget);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30" role="region" aria-label="Student Dashboard">
      <div className="space-y-5 md:space-y-6 p-4 sm:p-5 md:p-6 lg:p-8">
        {/* Header Section */}
        <header className="flex flex-col space-y-4">
          <div className="flex flex-col space-y-3 md:flex-row md:items-start md:justify-between md:space-y-0">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 sm:gap-3.5 mb-2">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25 flex-shrink-0">
                  <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight">
                    Student Dashboard
                  </h1>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Academic overview & resources
                  </p>
                </div>
              </div>
              <p className="text-sm md:text-base text-slate-600 mt-2 leading-relaxed">
                Welcome back, <span className="font-semibold text-slate-900">{user.name}</span>! View subjects offered in your program.
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2 md:mt-0">
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm flex-shrink-0 bg-blue-50 text-blue-700 border border-blue-200 shadow-sm">
                <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
                {user.course} - {
                  user.yearLevel === 1 ? '1st Year' :
                  user.yearLevel === 2 ? '2nd Year' :
                  user.yearLevel === 3 ? '3rd Year' :
                  `Year ${user.yearLevel || 'N/A'}`
                }
              </Badge>
            </div>
          </div>

          {/* Navigation Bar */}
          <nav aria-label="Student dashboard navigation" className="flex items-center justify-between mt-3 pb-1 border-b border-slate-100">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2 text-slate-800">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
              {getCurrentPageTitle()}
            </h2>
            {/* Mobile-only menu sheet */}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="default" className="gap-2 shadow-sm hover:shadow-md transition-all md:hidden h-10 px-4">
                  <Menu className="h-4 w-4" />
                  <span className="text-sm font-medium">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 sm:w-80">
                <SheetHeader>
                  <SheetTitle className="text-lg">Navigation Menu</SheetTitle>
                  <SheetDescription>
                    Select a section to view
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-1.5">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.value;
                    return (
                      <Button
                        key={item.value}
                        variant={isActive ? "secondary" : "ghost"}
                        className={`w-full justify-start transition-all duration-200 text-base h-12 ${isActive ? 'bg-blue-50 text-blue-700 shadow-sm font-semibold' : 'hover:bg-slate-50 text-slate-600'}`}
                        onClick={() => handleMenuItemClick(item.value)}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.label}
                        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                      </Button>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          </nav>
        </header>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Desktop inline tab navigation */}
          <div className="hidden md:block">
            <TabsList className="grid grid-cols-3 w-full max-w-lg h-auto bg-slate-100/80 rounded-xl p-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className="flex items-center gap-2 py-3 px-4 text-sm font-medium rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm transition-all"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <section aria-label="Student statistics">
              <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <Card
                      key={index}
                      className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 overflow-hidden relative focus-within:ring-2 focus-within:ring-blue-400"
                      tabIndex={0}
                      aria-label={stat.title + ': ' + stat.value}
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-[0.06] transition-opacity duration-300" style={{ background: stat.color }} />
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3 relative z-10 p-4 sm:p-5 lg:p-6">
                        <div className="space-y-1.5 flex-1 min-w-0">
                          <CardTitle className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wide">
                            {stat.title}
                          </CardTitle>
                          <div className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 truncate">{stat.value}</div>
                        </div>
                        <div className={`rounded-xl p-2.5 sm:p-3 text-white bg-gradient-to-br ${stat.color} group-hover:scale-110 transition-transform duration-300 shadow-lg flex-shrink-0 flex items-center justify-center`}>
                          <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* Info Cards */}
            <section aria-label="Program info and quick actions">
              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <Card className="hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2.5 text-base sm:text-lg font-bold text-slate-800">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      Program Information
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500">Your academic details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { label: 'Course/Program', value: user.course || 'Not assigned' },
                        { label: 'Year Level', value: getYearLevelLabel(user.yearLevel) },
                        { label: 'Current Semester', value: currentSemester },
                        { label: 'Academic Year', value: `S.Y. ${academicYear}` }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 sm:p-3.5 bg-gradient-to-r from-slate-50 to-blue-50/50 rounded-xl border border-slate-100 hover:border-blue-200 transition-colors gap-3">
                          <span className="text-sm font-medium text-slate-500">{item.label}</span>
                          <span className="text-sm font-semibold text-slate-800 text-right">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2.5 text-base sm:text-lg font-bold text-slate-800">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="h-4 w-4 text-white" />
                      </div>
                      Quick Actions
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500">Common student tasks</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2.5">
                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-sm group hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all h-12"
                      onClick={() => setActiveTab('subjects')}
                    >
                      <span className="flex items-center">
                        <BookOpen className="mr-2.5 h-4 w-4" />
                        View Subjects Offered
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-sm group hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-all h-12"
                      onClick={() => setActiveTab('archive')}
                    >
                      <span className="flex items-center">
                        <Archive className="mr-2.5 h-4 w-4" />
                        View Document Archive
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </section>
          </TabsContent>

          <TabsContent value="subjects">
            <SubjectsOffering studentId={user.id} yearLevel={user.yearLevel} course={user.course} />
          </TabsContent>

          <TabsContent value="archive">
            <DigitalArchive userRole="student" currentUserId={user.id} currentUserName={user.name} studentCourse={user.course} />
          </TabsContent>
        </Tabs>

        <AIAssistant user={user} />
      </div>
    </div>
  );
}